import { FileOpener } from "@capacitor-community/file-opener";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { isCapacitorNative } from "@/utils/platformUtils";

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(",")[1] ?? "");
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/** User closed share sheet — file already saved; not a failure. */
const isShareDismissed = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "object" &&
          error !== null &&
          "message" in error &&
          typeof (error as { message: unknown }).message === "string"
        ? (error as { message: string }).message
        : String(error ?? "");
  return /share cancel+ed|user cancel+ed|share dismissed/i.test(message);
};

/** Write blob to cache and open with the system viewer (PDF, etc.). */
export const openBlobInNativeViewer = async (
  blob: Blob,
  fileName: string,
  contentType: string,
): Promise<void> => {
  const base64Data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({
    path: fileName,
    data: base64Data,
    directory: Directory.Cache,
    recursive: true,
  });
  await FileOpener.open({
    filePath: uri,
    contentType,
    openWithDefault: false,
  });
};

/**
 * Web: `<a download>`.
 * Native: persist to Documents, then Share sheet (Save / Drive / WhatsApp…).
 * Does not require an app that can open the file type.
 */
export const downloadBlob = async (
  blob: Blob,
  fileName: string,
  _contentType: string,
): Promise<void> => {
  if (!isCapacitorNative()) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    return;
  }

  const base64Data = await blobToBase64(blob);

  await Filesystem.writeFile({
    path: fileName,
    data: base64Data,
    directory: Directory.Documents,
    recursive: true,
  });

  const { uri } = await Filesystem.getUri({
    directory: Directory.Documents,
    path: fileName,
  });

  try {
    await Share.share({
      title: fileName,
      files: [uri],
      dialogTitle: fileName,
    });
  } catch (error) {
    if (isShareDismissed(error)) {
      // File already in Documents — dismiss is OK.
      return;
    }
    throw error;
  }
};

export const openPdfInNativeViewer = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  await openBlobInNativeViewer(blob, fileName, "application/pdf");
};

export const openPdfBlob = async (
  blob: Blob,
  fileName: string,
): Promise<void> => {
  if (isCapacitorNative()) {
    await openPdfInNativeViewer(blob, fileName);
    return;
  }

  const pdfUrl = URL.createObjectURL(blob);
  window.open(pdfUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);
};
